<template>
  <div class="panel mt-00 p-0">
    <!-- Header -->
    <div class="px-4 py-3 bg-blue-dark text-white">
      <div class="flex items-center justify-between">
        <div>
          <p class="m-0 text-sm mb-1">
            <span class="font-semibold">{{ $t('activity') }}:</span>
            {{ pictogram.activity_planning_code + ' - ' + pictogram.activity_associated }}
          </p>
          <div class="flex items-center">
            <span class="text-sm font-semibold mr-1">{{ $t('pictogram') }}:</span>
            <span class="text-sm mr-3">{{ pictogram.name }}</span>
            <span class="text-sm font-semibold mr-2">{{ $t('pv_dashboard_progress') }}:</span>
            <div style="width: 200px; display: inline-block;">
              <b-progress
                :rounded="true"
                :value="parseFloat(pictogram.activity_progress)"
                type="is-success"
                show-value
                format="percent"
                size="is-medium"
              ></b-progress>
            </div>
          </div>
        </div>
        <b-button
          @click="$emit('go-back')"
          type="is-small is-primary"
          class="font-semibold uppercase">&lt;&lt; {{ $t("back") }}
        </b-button>
      </div>
    </div>

    <!-- Submenu layers -->
    <div class="flex items-center justify-center border-b border-blue-lighter mb-4"
      :class="{ 'opacity-50 pointer-events-none': isLoadingImage }">
      <div
        class="px-4 py-2 cursor-pointer text-sm font-semibold transition-colors"
        :class="activeLayer === null
          ? 'text-blue-dark border-b-2 border-blue-dark'
          : 'text-gray-500 hover:text-blue-dark'"
        @click="activeLayer = null"
      >
        <b-icon icon="th-large" pack="fas"></b-icon> <span class="text-sm font-semibold">{{ $t('all') }}</span>
      </div>
      <div
        v-for="lyr in pictogram.layers"
        :key="'tab-' + lyr.code"
        class="px-4 py-2 cursor-pointer text-sm font-semibold transition-colors"
        :class="activeLayer === lyr.code
          ? 'text-blue-dark border-b-2 border-blue-dark'
          : 'text-gray-500 hover:text-blue-dark'"
        @click="activeLayer = lyr.code"
      >
        <b-icon :icon="lyr.icon" pack="fas"></b-icon> <span class="text-sm font-semibold"> {{ layerLabel(lyr) }} </span>
      </div>
    </div>

    <!-- Body -->
    <div class="flex space-x-4">
      <div class="w-full w-5/6-x pb-5 mb-5 px-5 mx-5 relative">
        <b-loading :is-full-page="false" :active="isLoadingImage"></b-loading>
        <b-image
            :src="imgPictogram.src"
            :alt="pictogram.name"
            @load="$emit('image-load')"
            @error="$emit('image-error')"
        ></b-image>
      </div>
      <div class="w-auto px-2 w-1/6-x flex flex-col space-y-2">
        <PopoverPanel
          v-for="btn in sideButtons"
          :key="btn.key"
          :title="btn.title"
          v-model="popoverStates[btn.key]"
          @input="onPopoverChange(btn.key, $event)"
        >
          <template #trigger>
            <b-button
              :type="popoverStates[btn.key] ? 'is-primary' : 'is-primary is-outlined'"
              :icon-right="btn.icon"
              icon-pack="fas"
              :disabled="isLoadingImage"
            />
          </template>
          <!-- Resumen -->
          <template v-if="btn.key === 'summary'">
            <SummaryCard
              :layers="filteredLayers"
              :company-alias="companyAlias"
              :get-total="getPartialElements"
              :get-executed="getPartialExecutedElements"
              :get-incidents="getPartialIncidentElements"
              :get-progress="getProgressLayer"
              :use-executed-ratio="companyAlias === 'x-elio'"
            />
          </template>
          <!-- Progreso por estados -->
          <template v-if="btn.key === 'progress'">
            <div v-for="lyr in filteredLayers" :key="'pop-progress-' + lyr.code" class="mb-3">
              <p class="font-semibold text-sm mb-1">{{ layerLabel(lyr) }}</p>
              <div v-for="state in lyr.statuses" :key="state.id" class="flex items-center mb-1">
                <div class="w-3 h-3 rounded-sm mr-2" :style="'background-color: ' + state.color"></div>
                <span class="text-sm">{{ state.name }}: {{ getStatusProgress(lyr.code, state.id) }}</span>
              </div>
            </div>
          </template>
          <!-- Incidencias por estados -->
          <template v-if="btn.key === 'incidents'">
            <div v-for="lyr in filteredLayers" :key="'pop-incident-' + lyr.code" class="mb-3">
              <p class="font-semibold text-sm mb-1">{{ layerLabel(lyr) }}</p>
              <div v-for="state in lyr.incident_statuses" :key="state.id" class="flex items-center mb-1">
                <div class="w-3 h-3 rounded-sm mr-2" :style="'background-color: ' + state.color"></div>
                <span class="text-sm">{{ state.name }}: {{ getStatusProgress(lyr.code, state.id, 'incident') }}</span>
              </div>
            </div>
          </template>
        </PopoverPanel>
      </div>
      <!-- <div class="w-1/3">
        <SummaryTable
          :layers="filteredLayers"
          :company-alias="companyAlias"
          :get-total="getPartialElements"
          :get-executed="getPartialExecutedElements"
          :get-incidents="getPartialIncidentElements"
          :get-progress="getProgressLayer"
          :use-executed-ratio="companyAlias === 'x-elio'"
        >
          <template #layer-action="{ layer }">
            <span v-if="imgPictogram.type != layer.code" class="cursor-pointer text-blue-light"
              @click="$emit('show-image', layer.code)">
              <b-icon icon="eye" pack="fas"></b-icon>
            </span>
          </template>
        </SummaryTable>
      </div> -->
    </div>

    <div class="px-5 py-5">
      <!-- Skeletons mientras carga -->
      <template v-if="isLoadingImage">
        <div class="mt-5 w-full"><b-skeleton width="40%" height="24px"></b-skeleton></div>
        <div class="flex space-x-4 mt-5">
          <div v-for="n in 3" :key="'sk-prog-' + n" class="w-1/3">
            <b-skeleton width="100%" height="20px" class="mb-2"></b-skeleton>
            <b-skeleton width="100%" height="16px" class="mb-1" :count="4"></b-skeleton>
          </div>
        </div>
        <div class="mt-5 w-full"><b-skeleton width="40%" height="24px"></b-skeleton></div>
        <div class="flex space-x-4 mt-5">
          <div v-for="n in 3" :key="'sk-inc-' + n" class="w-1/3">
            <b-skeleton width="100%" height="20px" class="mb-2"></b-skeleton>
            <b-skeleton width="100%" height="16px" class="mb-1" :count="3"></b-skeleton>
          </div>
        </div>
      </template>

      <!-- Contenido real -->
      <template v-else>
        <!-- Total progreso por estados -->
        <div class="mt-5 w-full"><h3>{{ $t('pv_dashboard_total_progress_by_status') }}</h3></div>
        <div class="flex space-x-4 mt-5">
          <div v-for="lyr in layers" :key="'progress-' + lyr.code" class="w-1/3">
            <table class="w-full border-blue-lighter border-collapse border">
              <thead class="bg-blue-lightest">
                <tr>
                  <th colspan="2" class="p-2">
                    {{ $t('pv_dashboard_status_of', { name: layerLabel(lyr) }) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="state in lyr.statuses" :key="state.id">
                  <td class="justify-start w-auto h-full p-2 align-middle border border-blue-lighter">
                    <div class="w-4 h-4 rounded-sm" :style="'background-color: ' + state.color"></div>
                  </td>
                  <td class="justify-start w-auto h-full p-2 align-middle border border-blue-lighter text-right">
                    {{ state.name }}: <span>{{ getStatusProgress(lyr.code, state.id) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Total incidencia por estados -->
        <div class="mt-5 w-full"><h3>{{ $t('total_incidents_by_status') }}</h3></div>
        <div class="flex space-x-4 mt-5">
          <div v-for="lyr in layers" :key="'incident-' + lyr.code" class="w-1/3">
            <table class="w-full border-blue-lighter border-collapse border">
              <thead class="bg-blue-lightest">
                <tr>
                  <th colspan="2" class="p-2">
                    {{ $t('pv_dashboard_incident_statuses_of', { name: layerLabel(lyr) }) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="state in lyr.incident_statuses" :key="state.id">
                  <td class="justify-start w-auto h-full p-2 align-middle border border-blue-lighter">
                    <div class="w-4 h-4 rounded-sm" :style="'background-color: ' + state.color"></div>
                  </td>
                  <td class="justify-start w-auto h-full p-2 align-middle border border-blue-lighter text-right">
                    {{ state.name }}: <span>{{ getStatusProgress(lyr.code, state.id, 'incident') }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Solaring: Ratios y tiempos de validación -->
        <div v-if="companyAlias === 'solaring'">
          <div v-for="lyr in layers" :key="'ratios-' + lyr.code" class="mb-6">
            <div class="mt-5 w-full"><h3>{{ $t('pv_dashboard_quality_ratios', { layer: layerLabel(lyr) }) }}</h3></div>
            <div class="flex space-x-4 mt-5">
              <div class="w-1/4">
                <table class="w-full border-blue-lighter border-collapse border">
                  <thead class="bg-blue-lightest">
                    <tr><th colspan="2" class="p-2">{{ $t('pv_dashboard_defects_resolved_pending') }}</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ $t('pv_dashboard_resolved') }}</td>
                      <td>{{ $t('pv_dashboard_pending') }}</td>
                    </tr>
                    <tr>
                      <td>{{ ratios[lyr.code]?.defects_resolved?.resolved ?? 0 }}</td>
                      <td>{{ ratios[lyr.code]?.defects_resolved?.pending ?? 0 }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="w-1/4">
                <table class="w-full border-blue-lighter border-collapse border">
                  <thead class="bg-blue-lightest">
                    <tr><th colspan="2" class="p-2">{{ $t('pv_dashboard_ratio_real_executed') }}</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ $t('pv_dashboard_ratio') }}</td>
                      <td>{{ truncateToFixed(ratios[lyr.code]?.ratio_advance_real_executed?.ratio ?? 0, 4) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="w-1/4">
                <table class="w-full border-blue-lighter border-collapse border">
                  <thead class="bg-blue-lightest">
                    <tr><th colspan="2" class="p-2">{{ $t('pv_dashboard_ratio_real_accepted') }}</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ $t('pv_dashboard_ratio') }}</td>
                      <td>{{ truncateToFixed(ratios[lyr.code]?.ratio_advance_real_released?.ratio ?? 0, 4) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="w-1/4">
                <table class="w-full border-blue-lighter border-collapse border">
                  <thead class="bg-blue-lightest">
                    <tr><th colspan="2" class="p-2">{{ $t('pv_dashboard_ratio_real_released') }}</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ $t('pv_dashboard_ratio') }}</td>
                      <td>{{ truncateToFixed(ratios[lyr.code]?.ratio_advance_real_accepted?.ratio ?? 0, 4) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="w-full">
            <div class="mt-5 w-full"><h3>{{ $t('pv_dashboard_validation_phase_lag') }}</h3></div>
            <table class="w-full border-blue-lighter border-collapse border mt-4">
              <thead class="bg-blue-lightest">
                <tr>
                  <th class="p-2 text-sm font-semibold text-blue-dark border border-blue-lighter">{{ $t("code") }}</th>
                  <th class="p-2 text-sm font-semibold text-blue-dark border border-blue-lighter">{{ $t("description") }}</th>
                  <th class="p-2 text-sm font-semibold text-blue-dark border border-blue-lighter">{{ $t('pv_dashboard_phase_range_days', { range: '1-3', days: $t('days') }) }}</th>
                  <th class="p-2 text-sm font-semibold text-blue-dark border border-blue-lighter">{{ $t('pv_dashboard_phase_range_days', { range: '4-5', days: $t('days') }) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="validationTimes.length === 0">
                  <td colspan="4" class="p-2 border border-blue-lighter text-center text-gray-600">
                    {{ $t('pv_dashboard_no_validation_times') }}
                  </td>
                </tr>
                <tr v-for="flow in validationTimes" :key="flow.id">
                  <td class="p-2 border border-blue-lighter">{{ flow.code }}</td>
                  <td class="p-2 border border-blue-lighter">{{ flow.description || '-' }}</td>
                  <td class="p-2 border border-blue-lighter text-right">{{ flow.first_group_days }}</td>
                  <td class="p-2 border border-blue-lighter text-right">{{ flow.second_group_days }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import SummaryTable from "./SummaryTable.vue";
import SummaryCard from "./SummaryCard.vue";
import PopoverPanel from "./PopoverPanel.vue";

export default {
  name: "ProgressDetail",

  components: { SummaryTable, SummaryCard, PopoverPanel },

  data() {
    return {
      activeLayer: null,
      popoverStates: {
        summary: false,
        progress: false,
        incidents: false,
      },
    };
  },

  props: {
    pictogram: { type: Object, required: true },
    companyAlias: { type: String, required: true },
    imgPictogram: { type: Object, required: true },
    isLoadingImage: { type: Boolean, default: false },
    ratios: { type: Object, default: () => ({}) },
    validationTimes: { type: Array, default: () => ([]) },
  },

  computed: {
    // Computed (no data) para que los títulos se re-traduzcan al cambiar de idioma.
    sideButtons() {
      return [
        { key: 'summary', icon: 'chart-bar', title: this.$t('pv_dashboard_summary') },
        { key: 'progress', icon: 'traffic-light', title: this.$t('progress_by_status') },
        { key: 'incidents', icon: 'exclamation-triangle', title: this.$t('pv_dashboard_incidents_by_status') },
      ];
    },

    filteredLayers() {
      if (this.activeLayer) {
        return this.pictogram.layers.filter(l => l.code === this.activeLayer);
      }
      return this.pictogram.layers;
    },

    layers() {
      return this.pictogram.layers;
    },
  },

  watch: {
    activeLayer(code) {
      if (code) {
        this.$emit('show-image', code);
      }
    },
  },

  methods: {
    // Nombre de la capa en el idioma del usuario: `lyr.name` viene de BD (en español),
    // así que traducimos por código (pilings, structures...) y usamos el nombre solo si no hay traducción.
    layerLabel(lyr) {
      return this.$te(lyr.code) ? this.$t(lyr.code) : lyr.name;
    },

    getPartialElements(lyr) {
      return parseFloat(this.pictogram.metadata["total_" + lyr.code]);
    },

    getPartialExecutedElements(lyr) {
      if (this.companyAlias === "solaring") {
        const layer = this.pictogram.layers.find((l) => l.code === lyr.code);
        const config = layer.metadata?.config;
        const avance_method = config?.statuses?.avance_method;

        if (avance_method === "grouped") {
          const nameToFind = "Ejecutado (subcontratista)";
          let result = null;
          for (const phase of Object.values(layer.metadata?.progress_statuses)) {
            const status = Object.values(phase.statuses).find((s) => s.name === nameToFind);
            if (status) {
              result = status;
              break;
            }
          }
          return result?.advance ?? 0;
        } else {
          const statuses = layer.metadata?.progress_statuses;
          const result = Object.values(statuses).find((s) => s.name === "Ejecutado (subcontratista)");
          return result?.advance ?? 0;
        }
      }
      return parseFloat(
        this.pictogram.metadata.total_progress
          ? this.pictogram.metadata.total_progress[lyr.code] ?? 0
          : 0
      );
    },

    getPartialIncidentElements(lyr) {
      const layer = this.pictogram.layers.find((l) => l.code === lyr.code);
      if (layer && layer.metadata && layer.metadata.incident_statuses) {
        let total = 0;
        for (const key in layer.metadata.incident_statuses) {
          if (layer.metadata.incident_statuses.hasOwnProperty(key)) {
            total += parseFloat(layer.metadata.incident_statuses[key].advance ?? 0);
          }
        }
        return total;
      }
      return 0;
    },

    getProgressLayer(lyr) {
      const metadata = lyr.metadata;
      if (metadata && metadata["progress_layer"] != null) {
        return parseFloat(metadata["progress_layer"]["advance_normalize"] ?? 0);
      }
      return 0;
    },

    getStatusProgress(type, idState, typeStatus = "progress") {
      if (this.companyAlias !== "ignis" && this.companyAlias !== "solaring") {
        if (this.pictogram.metadata.statuses_progress != null) {
          if (this.pictogram.metadata.statuses_progress[type])
            return this.pictogram.metadata.statuses_progress[type][idState] ?? 0;
        }
        return 0;
      }

      const lyr = this.pictogram.layers.find((layer) => layer.code === type);

      if (typeStatus === "progress") {
        if (lyr && lyr.metadata && lyr.metadata.progress_statuses) {
          const cfg = lyr.metadata.config.statuses;
          const statusProgress = lyr.metadata.progress_statuses;

          if (cfg.avance_method === "grouped") {
            for (const key in statusProgress) {
              if (statusProgress[key].statuses[idState]) {
                return statusProgress[key].statuses[idState].advance ?? 0;
              }
            }
            return 0;
          } else {
            return statusProgress[idState].advance ?? 0;
          }
        }
      } else if (typeStatus === "incident") {
        if (lyr && lyr.metadata && lyr.metadata.incident_statuses) {
          const incidentStatuses = lyr.metadata.incident_statuses;
          return incidentStatuses[idState] ? incidentStatuses[idState].advance ?? 0 : 0;
        }
      }

      return 0;
    },

    onPopoverChange(key, isOpen) {
      if (isOpen) {
        // Cerrar los demas popovers al abrir uno
        for (const k in this.popoverStates) {
          if (k !== key) this.$set(this.popoverStates, k, false);
        }
      }
    },

    truncateToFixed(num, decimals) {
      const factor = Math.pow(10, decimals);
      return Math.floor(num * factor) / factor;
    },
  },
};
</script>

